import React, { useEffect, useRef } from 'react';

const PDFModal = ({ isOpen, onClose, pdfUrl, fileName, fileType }) => {
  const iframeRef = useRef(null);

  const isImage = fileType && fileType.startsWith('image/');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  const fileIcon = isImage ? 'bi-file-earmark-image' : 'bi-file-earmark-pdf';

  return (
    <div
      className="position-fixed top-0 start-0 d-flex align-items-center justify-content-center"
      style={{
        backgroundColor: 'rgba(0,0,0,0.95)',
        zIndex: 99999,
        width: '100vw',
        height: '100vh',
      }}
      onClick={onClose}
    >
      <div
        className="w-100 h-100 d-flex flex-column bg-white"
        style={{ maxWidth: '100vw', maxHeight: '100vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-dark text-white p-3 d-flex justify-content-between align-items-center" style={{ minHeight: '60px' }}>
          <div className="d-flex align-items-center">
            <i className="bi bi-file-earmark-pdf fs-4 me-2"></i>
            <h5 className="mb-0">{fileName || 'PDF Document'}</h5>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-light btn-sm text-white"
              onClick={handleDownload}
              title="Download PDF"
            >
              <i className="bi bi-download me-1"></i>
              Download
            </button>
            <button
              type="button"
              className="btn btn-outline-light btn-sm text-white"
              onClick={onClose}
              title="Close (ESC)"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow-1 position-relative overflow-auto" style={{ height: 'calc(100vh - 60px)' }}>
          {pdfUrl ? (
            isImage ? (
              // Image viewer — centered with padding, scrollable if large
              <div className="d-flex align-items-center justify-content-center w-100 h-100 p-3">
                <img
                  src={pdfUrl}
                  alt={fileName || 'Image'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                />
              </div>
            ) : (
              // PDF viewer
              <iframe
                ref={iframeRef}
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
                title={fileName || 'PDF Document'}
              />
            )
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="text-center">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                  style={{ width: '3rem', height: '3rem' }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="fs-5">Loading document...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFModal;